import { useEffect, useRef } from "react";
import { useSession } from "../lib/authClient";
import { isPostHogEnabled, posthog } from "../lib/posthog";

/**
 * PostHog identify/reset tied to the auth session. Identify ties anonymous
 * events to a stable distinct_id once a user signs in; reset on sign-out
 * re-anonymises so the next person on this device doesn't inherit the
 * identity. Rendered from main.tsx (client-only), so the SSR/prerender pass
 * never sees it.
 */
export function PostHogAuthSync() {
  const session = useSession();
  const user = session.data?.user;
  const isPending = session.isPending;

  const userId = user?.id;
  const email = user?.email;
  const name = user?.name;
  const emailVerified = user?.emailVerified;

  // Only reset on a real identified → signed-out transition. Resetting on
  // every anonymous visit would mint a fresh distinct_id per page load and
  // inflate unique-visitor counts.
  const prevUserId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isPostHogEnabled() || isPending) return;
    if (userId) {
      posthog.identify(userId, { email, name, emailVerified });
      prevUserId.current = userId;
    } else if (prevUserId.current) {
      posthog.reset();
      prevUserId.current = undefined;
    }
  }, [userId, email, name, emailVerified, isPending]);

  return null;
}
